/**
 * Remote record-id utilities for the PocketBase sync pipeline.
 *
 * PocketBase record ids are exactly 15 characters from [a-z0-9] (custom ids
 * supplied on create are validated against the same alphabet/length). The
 * offline-first client must therefore MINT its cross-device identity in that
 * exact format at record-creation time — the id is generated on the device
 * (possibly offline), stored locally, and reused verbatim as the PB record id
 * when the outbox drains.
 *
 * History: forms used `${formId}-${Date.now()}-${uuid8}` instanceIds. Those
 * are NOT valid PB ids (hyphens + 40+ chars), so every forms CREATE push was
 * rejected with 400/422 before this module existed. SyncWorker remints
 * legacy invalid ids at push time (safe: no pre-existing server copies —
 * they could never have been created).
 */

const PB_ALPHABET = 'abcdefghijklmnopqrstuvwxyz0123456789';
export const PB_ID_LENGTH = 15;

/**
 * Generate a PocketBase-valid record id (15 chars, [a-z0-9]).
 * Uses crypto.getRandomValues when available (browser + Node 19+), falling
 * back to Math.random (still ~78 bits of entropy across 15 base36 chars —
 * sufficient for client-minted record identities).
 */
export function generateRemoteId() {
  let id = '';
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const bytes = new Uint8Array(PB_ID_LENGTH);
    crypto.getRandomValues(bytes);
    for (let i = 0; i < PB_ID_LENGTH; i++) {
      id += PB_ALPHABET[bytes[i] % PB_ALPHABET.length];
    }
    return id;
  }
  for (let i = 0; i < PB_ID_LENGTH; i++) {
    id += PB_ALPHABET[Math.floor(Math.random() * PB_ALPHABET.length)];
  }
  return id;
}

/**
 * True when `value` can serve as a PocketBase record id
 * (exactly 15 lowercase alphanumerics).
 */
export function isPocketBaseId(value) {
  return (
    typeof value === 'string' &&
    value.length === PB_ID_LENGTH &&
    /^[a-z0-9]+$/.test(value)
  );
}
